import { renderToStaticMarkup } from 'react-dom/server'
import { createIntl } from 'react-intl'
import messages, { FormattedMessage, withValues } from 'shared/messages/messages';

import {
  KIOSK_PRICINGS_URL,
  KIOSK_PRICING_URL
} from 'shared/utils/Constants'

import { get, post, put, destroy } from './ApiService';

export const BASIC_PRICING_MODEL = 0;
export const TIERED_PRICING_MODEL = 1;
export const VOLUME_PRICING_MODEL = 2;
export const PACKAGES_PRICING_MODEL = 3;

export const getPurchaseDescription = (digitals, prints = 0, unlockAll = false, printsEnabled = false) => {
  if (unlockAll) {
    if (printsEnabled) {
      return messages.cart_unlock_all_prints;
    } else {
      return messages.cart_unlock_all;
    }
  }

  if (digitals > 0 && prints > 0) {
    return withValues(messages.cart_digitals_and_prints, { digitals, prints });
  } else if (digitals > 0) {
    return withValues(messages.cart_digitals, { quantity: digitals });
  } else if (prints > 0) {
    return withValues(messages.cart_prints, { quantity: prints });
  }
}

export const renderPurchaseDescription = (digitals, prints, unlockAll, printsEnabled) => {
  let message = getPurchaseDescription(digitals, prints, unlockAll, printsEnabled);
  return <FormattedMessage {...message} />;
}

const hotfixCartDigitalsAndPrintsMessage = (intl, message) => {
  const { values } = message;

  let digitalsValues = { ...values, quantity: values.digitals };
  let digitalsProps = {
    ...message,
    id: 'cart_digitals',
    values: digitalsValues
  };
  let digitals = intl.formatMessage(digitalsProps, digitalsProps.values);

  let printsValues = { ...values, quantity: values.prints };
  let printsProps = {
    ...message,
    id: 'cart_prints',
    values: printsValues
  };
  let prints = intl.formatMessage(printsProps, printsProps.values);

  return `${digitals} & ${prints}`;
}

export const renderPurchaseDescriptionStatic = (locale, compiledMessages, digitals, prints, unlockAll, printsEnabled) => {
  const intl = createIntl({ locale, messages: compiledMessages });
  let message = getPurchaseDescription(digitals, prints, unlockAll, printsEnabled);

  let description;

  // hotfix - cart_digitals_and_prints doesn't render properly.
  // jerry-rigging it here
  if (message.id === 'cart_digitals_and_prints') {
    description = hotfixCartDigitalsAndPrintsMessage(intl, message);
  } else {
    description = intl.formatMessage(message, message.values);
  }

  return renderToStaticMarkup(description);
}

export const getStandardPrice = (user, quantity) => {
  return user.price * quantity;
}

export const getDiscountedPriceForAll = (user, previousPurchases) => {
  let previousPayment = 0;
  if (previousPurchases) {
    previousPayment = previousPurchases.reduce((a, b) => a + b.total, 0);
  }

  return user.discount - previousPayment;
}

export const getDiscountedPrice = (user, quantity, previousPurchases) => {
  let priceForAll = getDiscountedPriceForAll(user, previousPurchases);

  let price = 0;
  switch (user.pricing_model) {
    case BASIC_PRICING_MODEL:
      price = getStandardPrice(user, quantity);
      break;

    case TIERED_PRICING_MODEL:
      console.log('Tiered pricing not implemented');
      break;

    case VOLUME_PRICING_MODEL:
      console.log('Volume pricing not implemented');
      break;

    case PACKAGES_PRICING_MODEL:
      let tier = null;
      for (let t of user.pricing_tiers) {
        if (t.requirement <= quantity) {
          tier = t;
        } else {
          break;
        }
      }
      if (tier) {
        price = tier.price * 100;
      } else {
        price = getStandardPrice(user, quantity);;
      }
  }

  if (user.discount !== 0 && priceForAll < price) {
    return priceForAll;
  } else {
    return price;
  }
}

export const formatCurrency = (cents, currency, showChange = false) => {
  let locale = getCurrencyLocale(currency)
  let amount = cents / 100;
  let effCurrency = currency || 'USD'
  let options = {
    style: 'currency',
    currency: effCurrency,
    minimumFractionDigits: showChange ? 2 : 0
  }

  // Overriding localized formatting
  if (currency === 'AED') {
    return amount + getCurrencySign(currency) + " " + effCurrency;
  }

  return new Intl.NumberFormat(locale, options).format(amount) + " " + effCurrency;
}

export const getCurrencySign = (currency) => {
  switch (currency) {
    case 'GBP':
      return '£';
    case 'EUR':
      return '€';
    case 'MYR':
      return 'RM';
    case 'BRL':
      return 'R$';
    case 'PLN':
      return 'zł';
    case 'AED':
      return 'د.إ';
    case 'HUF':
      return 'Ft';
    case 'CZK':
      return 'Kč';
    case 'percentage':
      return '%';
    default:
      return '$';
  }
}

export const getCurrencyLocale = currency => {
  switch (currency) {
    case 'CAD':
      return 'en-CA'; // 'fr-CA' for French
    case 'DOP':
      return 'es-DO';
    case 'EUR':
      return 'de-DE' // 'fr-FR' for French
    case 'GBP':
      return 'en-GB';
    case 'MOP':
      return 'zh-CN';
    case 'MXN':
      return 'es-MX';
    case 'MYR':
      return 'ms-MY'
    case 'USD':
      return 'en-US';
    case 'BRL':
      return 'pt-BR';
    case 'PLN':
      return 'pl-PL';
    case 'AED':
      return 'ar-AE';
    case 'AUD':
      return 'en-AU';
    case 'HUF':
      return 'hu-HU';
    case 'CZK':
      return 'cs-CZ';
    default:
      return 'en-US';
  }
}

export const formatPercent = cents => {
  return `${cents}%`;
}

export const getKioskPricings = async (kioskId) => {
  return await get(KIOSK_PRICINGS_URL(kioskId));
}

export const createKioskPricing = async (kioskId, params) => {
  return await post(KIOSK_PRICINGS_URL(kioskId), params);
}

export const updateKioskPricing = async (kioskId, pricingId, params) => {
  return await put(KIOSK_PRICING_URL(kioskId, pricingId), params);
}

export const deleteKioskPricing = async (kioskId, pricingId) => {
  return await destroy(KIOSK_PRICING_URL(kioskId, pricingId));
}
